import { createRoutine } from 'redux-saga-routines'
import { call, cancelled, put, select, takeEvery, takeLatest } from '@redux-saga/core/effects'
import * as service from 'services/portfolioDocumentsService'
import { toast } from 'react-toastify'
import { getApiErrors } from 'utils/errors'
import { getCurrentUser } from 'features/auth/ducks/selectors'

export const getPortfolioFolderTreeRoutine = createRoutine('GET_PORTFOLIO_FOLDER_TREE')
export const getPortfolioFolderFilesRoutine = createRoutine('GET_PORTFOLIO_FOLDER_FILES')
export const getPortfolioFolderUserCountsRoutine = createRoutine('GET_PORTFOLIO_FOLDER_USER_COUNTS')
export const createPortfolioFolderRoutine = createRoutine('CREATE_PORTFOLIO_FOLDER')
export const updatePortfolioFolderRoutine = createRoutine('UPDATE_PORTFOLIO_FOLDER')
export const deletePortfolioFolderRoutine = createRoutine('DELETE_PORTFOLIO_FOLDER')
export const movePortfolioFolderRoutine = createRoutine('MOVE_PORTFOLIO_FOLDER')
export const togglePortfolioFolderAccessRoutine = createRoutine('TOGGLE_PORTFOLIO_FOLDER_ACCESS')
export const uploadPortfolioDocumentsRoutine = createRoutine('UPLOAD_PORTFOLIO_DOCUMENTS')
export const movePortfolioFileRoutine = createRoutine('MOVE_PORTFOLIO_FILE')
export const unassignPortfolioFileRoutine = createRoutine('UNASSIGN_PORTFOLIO_FILE')
export const bulkMovePortfolioFilesRoutine = createRoutine('BULK_MOVE_PORTFOLIO_FILES')
export const bulkRemovePortfolioFilesRoutine = createRoutine('BULK_REMOVE_PORTFOLIO_FILES')
export const loadPortfolioFilesByFolderRoutine = createRoutine('LOAD_PORTFOLIO_FILES_BY_FOLDER')
export const loadPortfolioFilesByCategoryRoutine = createRoutine('LOAD_PORTFOLIO_FILES_BY_CATEGORY')
export const loadPortfolioFilesByBpRoutine = createRoutine('LOAD_PORTFOLIO_FILES_BY_BP')
export const updatePortfolioLoadingProgressRoutine = createRoutine('UPDATE_PORTFOLIO_LOADING_PROGRESS')
export const resetPortfolioDocumentsRoutine = createRoutine('RESET_PORTFOLIO_DOCUMENTS')

let activePortfolioDocumentsController = null
const activePortfolioBpControllers = new Map()

function abortActivePortfolioDocumentsLoad() {
  if (activePortfolioDocumentsController) {
    activePortfolioDocumentsController.abort()
    activePortfolioDocumentsController = null
  }
}

function* getTree({ payload }) {
  yield put(getPortfolioFolderTreeRoutine.request())
  try {
    const { data } = yield call(service.getPortfolioFolderTree, payload)
    yield put(getPortfolioFolderTreeRoutine.success(data))
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(getPortfolioFolderTreeRoutine.failure(error))
  }
}

const folderQuery = (folderId) => (state) => state.portfolioDocuments?.filesByFolder?.[folderId]?.query || {}

export function* refetchFolderFiles(portfolioId, folderId) {
  const query = yield select(folderQuery(folderId))
  yield put(getPortfolioFolderFilesRoutine.trigger({ portfolioId, folderId, page: 1, ...query }))
}

export function* getFiles({ payload }) {
  const { portfolioId, folderId, page = 1, limit = 30, search, fileTypeIds } = payload
  yield put(getPortfolioFolderFilesRoutine.request())
  try {
    const { data } = yield call(service.getPortfolioFolderFiles, portfolioId, folderId, page, limit, { search, fileTypeIds })
    yield put(getPortfolioFolderFilesRoutine.success({ folderId, page, data: data.data, total: data.total, query: { search, fileTypeIds } }))
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(getPortfolioFolderFilesRoutine.failure(error))
  }
}

function* getUserCounts({ payload }) {
  yield put(getPortfolioFolderUserCountsRoutine.request())
  try {
    const { data } = yield call(service.getPortfolioFolderUserCounts, payload)
    yield put(getPortfolioFolderUserCountsRoutine.success(data))
  } catch (error) {
    yield put(getPortfolioFolderUserCountsRoutine.failure(error))
  }
}

function* createFolder({ payload }) {
  const { portfolioId, body } = payload
  yield put(createPortfolioFolderRoutine.request())
  try {
    yield call(service.createPortfolioFolder, portfolioId, body)
    yield put(createPortfolioFolderRoutine.success())
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    toast.success('Folder created')
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(createPortfolioFolderRoutine.failure(error))
  }
}

function* updateFolder({ payload }) {
  const { portfolioId, folderId, body } = payload
  yield put(updatePortfolioFolderRoutine.request())
  try {
    yield call(service.updatePortfolioFolder, portfolioId, folderId, body)
    yield put(updatePortfolioFolderRoutine.success())
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(updatePortfolioFolderRoutine.failure(error))
  }
}

function* deleteFolder({ payload }) {
  const { portfolioId, folderId } = payload
  yield put(deletePortfolioFolderRoutine.request())
  try {
    yield call(service.deletePortfolioFolder, portfolioId, folderId)
    yield put(deletePortfolioFolderRoutine.success())
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    toast.success('Folder deleted')
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(deletePortfolioFolderRoutine.failure(error))
  }
}

function* moveFolder({ payload }) {
  const { portfolioId, folderId, parentFolderId } = payload
  yield put(movePortfolioFolderRoutine.request())
  try {
    yield call(service.movePortfolioFolder, portfolioId, folderId, parentFolderId)
    yield put(movePortfolioFolderRoutine.success())
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(movePortfolioFolderRoutine.failure(error))
  }
}

function* toggleAccess({ payload }) {
  const { portfolioId, userId, folders } = payload
  yield put(togglePortfolioFolderAccessRoutine.request())
  try {
    yield call(service.togglePortfolioFolderAccess, portfolioId, userId, folders)
    yield put(togglePortfolioFolderAccessRoutine.success())
    yield put(getPortfolioFolderUserCountsRoutine.trigger(portfolioId))
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(togglePortfolioFolderAccessRoutine.failure(error))
  }
}

function* upload({ payload }) {
  const { portfolioId, body, folderId } = payload
  yield put(uploadPortfolioDocumentsRoutine.request())
  try {
    yield call(service.uploadPortfolioDocuments, portfolioId, body)
    yield put(uploadPortfolioDocumentsRoutine.success())
    if (folderId) {
      yield* refetchFolderFiles(portfolioId, folderId)
    }
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    toast.success('Documents uploaded')
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(uploadPortfolioDocumentsRoutine.failure(error))
  }
}

export function* moveFile({ payload }) {
  const { portfolioId, fileId, portfolioFolderId, fromFolderId } = payload
  yield put(movePortfolioFileRoutine.request())
  try {
    yield call(service.movePortfolioFile, portfolioId, fileId, portfolioFolderId)
    yield put(movePortfolioFileRoutine.success())
    if (fromFolderId) {
      yield* refetchFolderFiles(portfolioId, fromFolderId)
    }
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(movePortfolioFileRoutine.failure(error))
  }
}

export function* unassignFile({ payload }) {
  const { portfolioId, fileId, folderId } = payload
  yield put(unassignPortfolioFileRoutine.request())
  try {
    yield call(service.unassignPortfolioFile, portfolioId, fileId)
    yield put(unassignPortfolioFileRoutine.success({ folderId, fileIds: [fileId] }))
    if (folderId) {
      yield* refetchFolderFiles(portfolioId, folderId)
    }
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    toast.success('Document removed')
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(unassignPortfolioFileRoutine.failure(error))
  }
}

function* bulkMoveFiles({ payload }) {
  const { portfolioId, fileIds, portfolioFolderId, fromFolderId, folderName } = payload
  yield put(bulkMovePortfolioFilesRoutine.request())
  try {
    yield call(service.bulkMovePortfolioFiles, portfolioId, fileIds, portfolioFolderId)
    yield put(bulkMovePortfolioFilesRoutine.success())
    if (fromFolderId) {
      yield* refetchFolderFiles(portfolioId, fromFolderId)
    }
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    toast.success(`Moved ${fileIds.length} document${fileIds.length === 1 ? '' : 's'}${folderName ? ` to ${folderName}` : ''}`)
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(bulkMovePortfolioFilesRoutine.failure(error))
  }
}

function* bulkRemoveFiles({ payload }) {
  const { portfolioId, fileIds, folderId } = payload
  yield put(bulkRemovePortfolioFilesRoutine.request())
  try {
    yield call(service.bulkRemovePortfolioFiles, portfolioId, fileIds)
    yield put(bulkRemovePortfolioFilesRoutine.success({ folderId, fileIds }))
    if (folderId) {
      yield* refetchFolderFiles(portfolioId, folderId)
    }
    yield put(getPortfolioFolderTreeRoutine.trigger(portfolioId))
    toast.success(`Removed ${fileIds.length} document${fileIds.length === 1 ? '' : 's'}`)
  } catch (error) {
    const msg = getApiErrors(error)
    msg && toast.error(msg)
    yield put(bulkRemovePortfolioFilesRoutine.failure(error))
  }
}

function* loadPortfolioFilesByFolder({ payload }) {
  const { portfolioId, folderId, fileCategories, cursor = null, buildingPassportId, search, fileTypeIds, uploaderIds, tagIds } = payload
  abortActivePortfolioDocumentsLoad()
  const abortController = new AbortController()
  activePortfolioDocumentsController = abortController
  yield put(loadPortfolioFilesByFolderRoutine.request({ folderId }))
  try {
    const categoryIds = (fileCategories || [])
      .filter(cat => cat.folderId === folderId)
      .map(cat => cat.id)

    if (categoryIds.length === 0) {
      yield put(loadPortfolioFilesByFolderRoutine.success({ folderId, files: [], nextCursor: null, hasMore: false, replace: true }))
      return
    }

    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''
    categoryIds.forEach((catId, index) => {
      queryParams[`filter[category][${index}]`] = catId
    })
    if (search) queryParams['filter[search]'] = search
    if (fileTypeIds?.length) queryParams['filter[file_type_ids]'] = fileTypeIds.join(',')
    if (uploaderIds?.length) queryParams['filter[uploader_ids]'] = uploaderIds.join(',')
    if (tagIds?.length) queryParams['filter[tag_ids]'] = tagIds.join(',')
    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) queryParams['filter[hide_duplicates]'] = 1

    const { data } = yield call(service.getPortfolioFiles, { portfolioId, buildingPassportId, query: queryParams }, abortController.signal)

    yield put(loadPortfolioFilesByFolderRoutine.success({
      folderId,
      files: data?.data || [],
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor,
    }))
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadPortfolioFilesByFolderRoutine.failure({ folderId, error })); return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadPortfolioFilesByFolderRoutine.failure({ folderId, error }))
  } finally {
    if (yield cancelled()) abortController.abort()
    if (activePortfolioDocumentsController === abortController) activePortfolioDocumentsController = null
  }
}

function* loadPortfolioFilesByCategory({ payload }) {
  const { portfolioId, categoryId, cursor = null, buildingPassportId, search, fileTypeIds, uploaderIds, tagIds } = payload
  abortActivePortfolioDocumentsLoad()
  const abortController = new AbortController()
  activePortfolioDocumentsController = abortController
  yield put(loadPortfolioFilesByCategoryRoutine.request({ categoryId }))
  try {
    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''
    if (categoryId === 'uncategorised') queryParams['filter[uncategorised]'] = 'true'
    else if (categoryId !== 'all_files') queryParams['filter[category]'] = categoryId
    if (search) queryParams['filter[search]'] = search
    if (fileTypeIds?.length) queryParams['filter[file_type_ids]'] = fileTypeIds.join(',')
    if (uploaderIds?.length) queryParams['filter[uploader_ids]'] = uploaderIds.join(',')
    if (tagIds?.length) queryParams['filter[tag_ids]'] = tagIds.join(',')
    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) queryParams['filter[hide_duplicates]'] = 1

    const { data } = yield call(service.getPortfolioFiles, { portfolioId, buildingPassportId, query: queryParams }, abortController.signal)

    yield put(loadPortfolioFilesByCategoryRoutine.success({
      categoryId,
      files: data?.data || [],
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor,
    }))
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadPortfolioFilesByCategoryRoutine.failure({ categoryId, error })); return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadPortfolioFilesByCategoryRoutine.failure({ categoryId, error }))
  } finally {
    if (yield cancelled()) abortController.abort()
    if (activePortfolioDocumentsController === abortController) activePortfolioDocumentsController = null
  }
}

function* loadPortfolioFilesByBp({ payload }) {
  const { portfolioId, bpId } = payload
  const key = `bp:${bpId}`
  if (activePortfolioBpControllers.has(bpId)) {
    activePortfolioBpControllers.get(bpId).abort()
  }
  const abortController = new AbortController()
  activePortfolioBpControllers.set(bpId, abortController)
  yield put(loadPortfolioFilesByBpRoutine.request({ bpId }))
  try {
    let allFiles = []
    let page = 1
    let hasMoreFiles = true

    while (hasMoreFiles) {
      const queryParams = { page, limit: 3000 }

      const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
      if (hideDuplicates) {
        queryParams['filter[hide_duplicates]'] = 1
      }

      const { data } = yield call(service.getPortfolioFiles, {
        portfolioId,
        buildingPassportId: bpId,
        query: queryParams
      }, abortController.signal)

      const filesForPage = data?.data || []
      allFiles = [...allFiles, ...filesForPage]

      yield put(updatePortfolioLoadingProgressRoutine.success({ key, loaded: allFiles.length }))

      hasMoreFiles = filesForPage.length === 3000

      const uniqueFiles = Array.from(new Map(allFiles.map(file => [file.id, file])).values())
      yield put(loadPortfolioFilesByBpRoutine.success({ bpId, files: uniqueFiles, partial: hasMoreFiles }))

      page++
    }
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadPortfolioFilesByBpRoutine.failure({ bpId, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadPortfolioFilesByBpRoutine.failure({ bpId, error }))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
      yield put(loadPortfolioFilesByBpRoutine.failure({ bpId }))
    }
    if (activePortfolioBpControllers.get(bpId) === abortController) {
      activePortfolioBpControllers.delete(bpId)
    }
  }
}

export function* portfolioDocumentsSaga() {
  yield takeLatest(getPortfolioFolderTreeRoutine.TRIGGER, getTree)
  yield takeEvery(getPortfolioFolderFilesRoutine.TRIGGER, getFiles)
  yield takeLatest(getPortfolioFolderUserCountsRoutine.TRIGGER, getUserCounts)
  yield takeEvery(createPortfolioFolderRoutine.TRIGGER, createFolder)
  yield takeEvery(updatePortfolioFolderRoutine.TRIGGER, updateFolder)
  yield takeEvery(deletePortfolioFolderRoutine.TRIGGER, deleteFolder)
  yield takeEvery(movePortfolioFolderRoutine.TRIGGER, moveFolder)
  yield takeEvery(togglePortfolioFolderAccessRoutine.TRIGGER, toggleAccess)
  yield takeEvery(uploadPortfolioDocumentsRoutine.TRIGGER, upload)
  yield takeEvery(movePortfolioFileRoutine.TRIGGER, moveFile)
  yield takeEvery(unassignPortfolioFileRoutine.TRIGGER, unassignFile)
  yield takeEvery(bulkMovePortfolioFilesRoutine.TRIGGER, bulkMoveFiles)
  yield takeEvery(bulkRemovePortfolioFilesRoutine.TRIGGER, bulkRemoveFiles)
  yield takeLatest(loadPortfolioFilesByFolderRoutine.TRIGGER, loadPortfolioFilesByFolder)
  yield takeLatest(loadPortfolioFilesByCategoryRoutine.TRIGGER, loadPortfolioFilesByCategory)
  yield takeEvery(loadPortfolioFilesByBpRoutine.TRIGGER, loadPortfolioFilesByBp)
}
